import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, BarChart3, CalendarRange, LineChart, TrendingUp } from 'lucide-react'
import { getFacilities, type FacilityOption } from '../../lib/services/facilities'
import { getBenchmark, getTrends, type BenchmarkEntry, type TrendEntry } from '../../lib/services/chartAudit'

const WINDOW_OPTIONS = [
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '365 days' },
] as const

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString()
}

function formatPercent(value: number | null | undefined): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—'
  return `${Math.round(value)}%`
}

function getAveragePassRate(passRates: Record<string, number>): number | null {
  const values = Object.values(passRates)
  if (values.length === 0) return null
  return values.reduce((sum, rate) => sum + rate, 0) / values.length
}

export function ChartAuditTrendsPage() {
  const [facilities, setFacilities] = useState<FacilityOption[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState('')
  const [windowDays, setWindowDays] = useState<number>(365)
  const [trends, setTrends] = useState<TrendEntry[]>([])
  const [benchmark, setBenchmark] = useState<Record<string, BenchmarkEntry>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadFacilities() {
      try {
        const rows = await getFacilities()
        if (cancelled) return
        setFacilities(rows)
        if (rows.length > 0) setSelectedFacilityId((current) => current || rows[0].facility_id)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load facilities')
      }
    }

    loadFacilities()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!selectedFacilityId) return
    let cancelled = false

    async function loadTrendWorkspace() {
      try {
        setLoading(true)
        setError(null)
        const [trendResponse, benchmarkResponse] = await Promise.all([
          getTrends(selectedFacilityId, windowDays),
          getBenchmark(selectedFacilityId),
        ])
        if (cancelled) return
        setTrends(trendResponse.trends)
        setBenchmark(benchmarkResponse)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load chart-audit trends')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadTrendWorkspace()
    return () => { cancelled = true }
  }, [selectedFacilityId, windowDays])

  const latestTrend = trends[trends.length - 1] ?? null
  const selectedFacility = facilities.find((facility) => facility.facility_id === selectedFacilityId) ?? null
  const averagePassRate = latestTrend ? getAveragePassRate(latestTrend.pass_rates) : null

  const latestRuleRows = useMemo(() => {
    if (!latestTrend) return []
    return Object.entries(latestTrend.pass_rates)
      .map(([ruleId, facilityRate]) => ({
        ruleId,
        facilityRate,
        benchmark: benchmark[ruleId] ?? null,
      }))
      .sort((left, right) => left.facilityRate - right.facilityRate || left.ruleId.localeCompare(right.ruleId))
      .slice(0, 8)
  }, [benchmark, latestTrend])

  const timelineRows = useMemo(() => {
    return [...trends].reverse()
  }, [trends])

  return (
    <div className="space-y-6">
      <div className="v11-workspace-header flex items-center gap-4">
        <Link to="/chart-audit" className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
          <ArrowLeft size={18} />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Chart Audit Trends</h1>
          <p className="mt-1 text-sm text-slate-500">
            Facility-scoped trend history and tenant-scoped benchmark comparison from the landed chart-audit analytics routes.
          </p>
        </div>
      </div>

      <div className="v11-section flex flex-wrap items-end gap-3 p-4">
        <div className="min-w-[220px] flex-1">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
            Facility
            <select
              value={selectedFacilityId}
              onChange={(event) => setSelectedFacilityId(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            >
              {facilities.map((facility) => (
                <option key={facility.facility_id} value={facility.facility_id}>{facility.facility_name}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="min-w-[180px]">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500">
            Window
            <select
              value={windowDays}
              onChange={(event) => setWindowDays(Number(event.target.value))}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
            >
              {WINDOW_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="inline-flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500">
          <BarChart3 size={14} className="text-slate-400" />
          Benchmark cards show tenant-scoped percentile bands only.
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-sm text-slate-500">Loading chart-audit trends...</div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      ) : trends.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-12 text-center text-sm text-slate-500">
          No trend data is available yet for {selectedFacility?.facility_name ?? 'this facility'}.
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="v11-kpi-card">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Periods</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{trends.length}</p>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-teal">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Latest Charts</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{latestTrend?.total_charts ?? 0}</p>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-gold">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Latest Findings</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{latestTrend?.total_findings ?? 0}</p>
            </div>
            <div className="v11-kpi-card v11-kpi-accent-rose">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Avg Pass Rate</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{formatPercent(averagePassRate)}</p>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.1fr,0.9fr]">
            <div className="v11-section p-5">
              <div className="flex items-center gap-2">
                <CalendarRange size={18} className="text-slate-400" />
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Trend Timeline</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Latest-first history for {selectedFacility?.facility_name ?? 'the selected facility'}.
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {timelineRows.map((trend) => {
                  const avgRate = getAveragePassRate(trend.pass_rates)
                  const scoreOne = trend.score_distribution['1'] ?? 0
                  const scoreTwo = trend.score_distribution['2'] ?? 0
                  const scoreThree = trend.score_distribution['3'] ?? 0
                  return (
                    <div key={trend.batch_id} className="v11-dashboard-card p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <p className="font-semibold text-slate-900">{formatDate(trend.period_date)}</p>
                          <p className="text-xs text-slate-500">Batch {trend.batch_id.slice(0, 8)}...</p>
                        </div>
                        <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                          Avg pass {formatPercent(avgRate)}
                        </div>
                      </div>

                      <div className="mt-3 grid gap-3 text-xs text-slate-600 sm:grid-cols-3">
                        <span>Total charts: {trend.total_charts}</span>
                        <span>Total findings: {trend.total_findings}</span>
                        <span>Rules tracked: {Object.keys(trend.pass_rates).length}</span>
                      </div>

                      <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-slate-200">
                        {scoreOne > 0 ? <div className="bg-emerald-500" style={{ width: `${scoreOne}%` }} /> : null}
                        {scoreTwo > 0 ? <div className="bg-amber-400" style={{ width: `${scoreTwo}%` }} /> : null}
                        {scoreThree > 0 ? <div className="bg-rose-500" style={{ width: `${scoreThree}%` }} /> : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="space-y-4">
              <div className="v11-section p-5">
                <div className="flex items-center gap-2">
                  <TrendingUp size={18} className="text-slate-400" />
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Latest Rule Snapshot</h2>
                    <p className="mt-1 text-xs text-slate-500">
                      Lowest-performing rules from the latest trend period with tenant-scoped benchmark bands.
                    </p>
                  </div>
                </div>

                <div className="mt-4 space-y-3">
                  {latestRuleRows.map((row) => (
                    <div key={row.ruleId} className="v11-dashboard-card p-4">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold text-slate-900">{row.ruleId}</span>
                        <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                          Facility {formatPercent(row.facilityRate)}
                        </span>
                      </div>
                      <div className="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
                        <span>P25 {formatPercent(row.benchmark?.p25 ?? null)}</span>
                        <span>P50 {formatPercent(row.benchmark?.p50 ?? null)}</span>
                        <span>P75 {formatPercent(row.benchmark?.p75 ?? null)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="v11-section p-5">
                <div className="flex items-center gap-2">
                  <LineChart size={18} className="text-slate-400" />
                  <div>
                    <h2 className="text-lg font-bold text-slate-900">Guardrails</h2>
                    <p className="mt-1 text-xs text-slate-500">This route stays within the landed chart-audit contract.</p>
                  </div>
                </div>
                <ul className="mt-4 space-y-2 text-sm text-slate-600">
                  <li>Benchmark values are derived only from the current tenant portfolio.</li>
                  <li>No peer facility identities or cross-tenant aggregates are exposed.</li>
                  <li>Trend history reflects existing per-batch `chart_audit_trend_data` only.</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
